import { useCallback, useEffect } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import { PresentationRequestHeader } from '@Components/index';
import {
  useAppDispatch,
  useAppSelector,
  useHasAccessToSubmit,
  useUserPermissions,
} from '@Hooks/index';
import {
  RequestPathPerType,
  RequestTypePerPath,
} from '@Features/router/ess/enum/RequestsRoutePaths.enum';
import { Can } from '@Components/Can';
import { MButton } from '@Components/Base';
import { ReactComponent as PlusAddIcon } from '@Icons/additional/plus-add.svg';
import { useGetRequestsCountMutation } from '@Store/api/basicHrmApiSlice';
import { headerActions } from '@Store/slices/headerSlice';
import { requestTypesMap } from '@Enum/RequestTypesMap';
import { extractRequestTypeCode } from '@Features/utils/extracterPathnames';
import {
  currentSelectedRequest,
  getRequestTypeOptions,
} from '@Features/store/selectors/manageRequestSelector';
import { manageRequestActions } from '@Features/store/ManageRequestSlice';

import styles from './manage-requests.module.css';

const ManageRequests: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const selectedRequest = useAppSelector(currentSelectedRequest);
  const requestTypesOptions = useAppSelector(getRequestTypeOptions);
  const requestTypeSelected = requestTypesMap[selectedRequest];

  const hasAccessToSubmit = useHasAccessToSubmit(requestTypeSelected);

  const navigate = useNavigate();

  const [getRequestsCount] = useGetRequestsCountMutation();

  const permissions = useUserPermissions();

  const allowedRequests = requestTypesOptions.filter((request) =>
    permissions.can('List', requestTypesMap[request.label])
  );

  useEffect(() => {
    const requestTypeCode = extractRequestTypeCode(
      pathname,
      RequestPathPerType
    );

    const requestTypeLabel = requestTypesOptions.find(
      (requestTypesOption) => requestTypesOption?.value === requestTypeCode
    )?.label;

    dispatch(manageRequestActions.setSelectedRequest(requestTypeLabel || null));

    if (
      allowedRequests.length &&
      !allowedRequests.some((request) => request?.value === requestTypeCode)
    ) {
      navigate(
        `/ess/manage-requests/${RequestTypePerPath[allowedRequests[0]?.value]}`
      );
    }
  }, [pathname, requestTypesOptions]);

  const handleSelect = useCallback(
    (value: string) => {
      const selectedOption = requestTypesOptions.find(
        (requestTypesOption) => requestTypesOption.label === value
      );

      if (!selectedOption) {
        return;
      }

      navigate(
        `/ess/manage-requests/${RequestTypePerPath[selectedOption?.value]}`
      );
    },
    [requestTypesOptions]
  );

  const fetchRequestsCount = useCallback(() => {
    getRequestsCount()
      .unwrap()
      .then((response) => {
        dispatch(headerActions.setRequestsCount(response));
      });
  }, []);

  return (
    <>
      {!isRequestFormPage && (
        <PresentationRequestHeader
          selectOptions={allowedRequests}
          selectedValue={selectedRequest}
          caption="My Requests"
          handleSelect={handleSelect}
          onRequestSelectorOpen={fetchRequestsCount}
        >
          {hasAccessToSubmit && (
            <Can I="Create" a={requestTypesMap[selectedRequest]}>
              <MButton
                onClick={() => {
                  if (!selectedRequest) {
                    return;
                  }

                  navigate(`${pathname}/create`);
                }}
                type="primary"
                className={styles.addNewRequestButton}
              >
                <div>
                  <PlusAddIcon />
                </div>
                Add
              </MButton>
            </Can>
          )}
        </PresentationRequestHeader>
      )}
      <Outlet context={{ isRequestFormPage }} />
    </>
  );
};

export default ManageRequests;
